/**
 * 数据块「数值」输入的解析与改块建议(2026-07-30,7.29 反馈任务 8)。
 *
 * ═══ 缺陷 ═══════════════════════════════════════════════════════════════════
 * 「数据对比」块里填文字,不报错、直接变成 0 —— `value: Number(it.value) || 0`。
 * 用户以为自己填了东西,出图才发现是 0。他真正想做的是「我家 vs 别人家」的**文字**对照,
 * 而那种块**本来就有**(前后对比 / 数据卡 / 百分比,它们的 value 本来就是 string)。
 *
 * 所以这里要办两件事:
 *   ① **可见的失败优于隐形降级** —— 非数字当场说出来,而且**不许落 0**。
 *   ② 说清楚该去哪 —— 指名「前后对比」块,并支持一键切过去。
 */
import { STYLE_META } from "./blocks";

/** 这些块的 value 本来就是 string,能装文字。 */
export const TEXT_VALUE_STYLE_TAGS = ["beforeafter", "stat", "percent"] as const;

/** 非数字时推荐切到哪个块(取第一个存在于样式表里的)。 */
export const TEXT_PAIR_FALLBACK_TAG =
  TEXT_VALUE_STYLE_TAGS.find((t) => STYLE_META[t]) ?? "beforeafter";

export interface NumericParse {
  ok: boolean;
  /** 解析成功时的数值;失败时为 null(★不是 0 —— 落 0 正是本缺陷) */
  value: number | null;
  /** 失败原因(直接给用户看) */
  error?: string;
}

/**
 * 「数值」输入解析。空串当作「还没填」(ok，值 null),不报错 —— 用户正在敲字。
 *
 * 接受:整数、小数、负数、前后空白、千分位逗号(1,234)、末尾百分号(35%→35)。
 * 拒绝:任何含其他字符的输入 —— 那是文字,画不成横条。
 */
export function parseSeriesValue(raw: string): NumericParse {
  const s = (raw ?? "").trim();
  if (s === "") {
    return { ok: true, value: null };
  }
  const cleaned = s.replace(/,/g, "").replace(/%$/, "");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return {
      ok: false,
      value: null,
      error: `「${s}」不是数字 —— 这一块画横条图,只吃数字。要做文字对照请用「${
        STYLE_META[TEXT_PAIR_FALLBACK_TAG]?.label ?? "前后对比"
      }」块。`,
    };
  }
  const n = Number(cleaned);
  if (!Number.isFinite(n)) {
    return { ok: false, value: null, error: `「${s}」不是有效数字` };
  }
  return { ok: true, value: n };
}

/** 数值 → 输入框里显示的字符串(null/undefined 显示空,别显示 "0" 冒充用户填过)。 */
export function formatSeriesValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  return String(v);
}

/**
 * 样式 → 表单家族(与 BlockInspector 的分派同一张表)。
 *
 * 换块搬运字段时要按家族判,不能按「新样式的默认值里有没有这个键」—— 默认骨架本来就不给
 * 可选字段(如 title),那样判等于永远搬不过去。
 */
export function familyOfStyle(styleTag: string): string {
  const meta = STYLE_META[styleTag];
  if (!meta) return "text";
  if (meta.primitive === "image") return "image";
  if (styleTag === "numlist" || styleTag === "twocol") return "list";
  if (["comparebar", "line", "pie"].includes(styleTag)) return "chart";
  if (["stat", "percent", "beforeafter"].includes(styleTag)) return "stat";
  if (styleTag === "table") return "table";
  if (styleTag === "rating") return "rating";
  if (styleTag === "qr") return "qr";
  if (meta.primitive === "data") return "pairs";
  return "text";
}

/** 这些家族的 schema 里有可选 `title`(ai 侧 PairsProps / StatProps / ChartProps)。 */
export const TITLE_FAMILIES = ["pairs", "stat", "chart"];

/**
 * 换块时把「名称/数值」对搬过去。目标块的 value 是 string,所以数字原样转字符串;
 * 没填过的(null)搬成空串,不要凭空造 0。
 */
export function pairsForTextBlock(
  items: Array<{ label: string; value: unknown }>
): Array<{ value: string; label: string }> {
  return items.map((it) => ({
    label: String(it.label ?? ""),
    value: formatSeriesValue(it.value),
  }));
}
